import { useEffect, useRef, useState } from 'react'
import { Icone } from '@/components/ui/Icone'

/**
 * La loupe — une image en plein écran, agrandissable.
 *
 * **Pourquoi elle existe.** Les schémas de botanique — une coupe de racine, les
 * quatre phases de la mitose — portent des légendes de huit points. Sur un
 * téléphone de cinq pouces, l'image tient dans la colonne de texte et le
 * candidat ne lit aucune légende. Il n'a alors que deux issues : renoncer, ou
 * quitter l'application pour ouvrir son livre.
 *
 * **Le zoom se fait par paliers, pas au pincement.** Le pincement natif est
 * neutralisé dans une surface fixe par la plupart des navigateurs mobiles, et
 * le réimplémenter à la main donne un geste qui ressemble au vrai sans se
 * comporter comme lui — ce qui est pire que de ne pas l'avoir. Trois paliers,
 * atteignables à la tape, au clavier et au bouton : le déplacement se fait
 * ensuite au défilement, qui lui fonctionne partout.
 *
 * **Échap ferme, et le focus revient d'où il venait.** Une surface qui prend
 * tout l'écran sans en sortir enferme qui navigue au clavier.
 */
const PALIERS = [1, 2, 3] as const

export function Loupe({ src, alt, onFermer }: { src: string; alt: string; onFermer: () => void }) {
  const [palier, setPalier] = useState(0)
  const fermeture = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFermer()
      if (e.key === '+' || e.key === '=') setPalier((p) => Math.min(p + 1, PALIERS.length - 1))
      if (e.key === '-') setPalier((p) => Math.max(p - 1, 0))
    }

    document.addEventListener('keydown', auClavier)
    fermeture.current?.focus()

    // Le fond ne défile pas pendant qu'on regarde : sans cela, refermer la
    // loupe rend une page qui a bougé toute seule.
    const defilement = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', auClavier)
      document.body.style.overflow = defilement
    }
  }, [onFermer])

  const zoom = PALIERS[palier] ?? 1

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: 'rgba(0, 0, 0, 0.92)' }}
    >
      <div className="flex items-center justify-between gap-2 p-3">
        <div className="flex gap-2">
          <Commande
            onClick={() => { setPalier((p) => Math.max(p - 1, 0)); }}
            disabled={palier === 0}
            label="Réduire"
          >
            −
          </Commande>
          <Commande
            onClick={() => { setPalier((p) => Math.min(p + 1, PALIERS.length - 1)); }}
            disabled={palier === PALIERS.length - 1}
            label="Agrandir"
          >
            +
          </Commande>
          <span className="self-center text-[13px]" style={{ color: 'rgba(255,255,255,0.7)' }}>
            ×{zoom}
          </span>
        </div>

        <Commande ref={fermeture} onClick={onFermer} label="Fermer">
          <Icone nom="croix" taille={18} />
        </Commande>
      </div>

      {/* `overflow-auto` plutôt qu'un déplacement à la souris : au-delà de la
          taille de l'écran, le défilement est le geste que tout le monde
          connaît déjà, et il marche au doigt comme à la molette. */}
      <div className="flex-1 overflow-auto p-3">
        <img
          src={src}
          alt={alt}
          onClick={() => { setPalier((p) => (p + 1) % PALIERS.length); }}
          className="mx-auto h-auto cursor-zoom-in"
          style={{ width: `${String(zoom * 100)}%`, maxWidth: zoom === 1 ? '100%' : 'none' }}
        />
      </div>

      {/* La description reste lisible EN MÊME TEMPS que l'image : c'est souvent
          elle qui dit ce qu'il faut regarder. */}
      <p className="p-3 text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>
        {alt}
      </p>
    </div>
  )
}

function Commande({
  ref,
  onClick,
  disabled = false,
  label,
  children,
}: {
  ref?: React.Ref<HTMLButtonElement>
  onClick: () => void
  disabled?: boolean
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      // 44 px : RG-11 impose cette zone tactile, et c'est encore plus vrai sur
      // un fond noir où l'on vise sans repère.
      className="flex h-11 w-11 items-center justify-center rounded-full text-[18px] disabled:opacity-40"
      style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}
    >
      {children}
    </button>
  )
}
